import { useLocation, useNavigate } from 'react-router-dom'

import { isSsoPath } from 'utils/auth'
import { IS_AUTHORIZED, LOGIN_PROVIDER, LOGOUT_REQUEST, PRA_TOKEN, REDIRECT_URL, ROUTES } from 'definitions'
import { jsonToQueryString } from 'utils/formatters'
import { useStores } from 'stores'

export const useAuthManager = () => {
  const { authStore, pageResourceStore, connectionCartStore } = useStores()
  const navigate = useNavigate()
  const location = useLocation()

  const getAuthSession = async () => {
    const result = await authStore.getAuthSession()
    if (result) {
      if (result.session.isClientBuyer) {
        sessionStorage.setItem(IS_AUTHORIZED, true)
        sessionStorage.setItem(PRA_TOKEN, result.session?.jwtoken)

        // Fetch account and common translations
        authStore.AppSession.loading = true
        await Promise.allSettled([pageResourceStore.get('account'), pageResourceStore.get('common')])
        authStore.AppSession.loading = false
      } else {
        window.location = process.env.REACT_APP_OAZO_ADMIN_URL
        return false
      }
    }
    return result
  }

  const getLoginUrl = provider => {
    switch (provider) {
      case LOGIN_PROVIDER.BELFIUS:
        return ROUTES.LOGIN_BELFIUS
      case LOGIN_PROVIDER.GOED:
        return ROUTES.LOGIN_GOED
      case LOGIN_PROVIDER.INTERPARKING:
        return ROUTES.LOGIN_INTERPARKING
      case LOGIN_PROVIDER.NUNHEMS:
        return ROUTES.LOGIN_NUNHEMS
      case LOGIN_PROVIDER.AUTOSPHERE:
        return ROUTES.LOGIN_AUTOSPHERE
      case LOGIN_PROVIDER.ARGENTA:
        return ROUTES.LOGIN_ARGENTA
      default:
        return ROUTES.LOGIN
    }
  }

  const getLoginProvider = pathname => {
    if (pathname)
      switch (pathname) {
        case ROUTES.LOGIN_BELFIUS:
          return LOGIN_PROVIDER.BELFIUS
        case ROUTES.LOGIN_GOED:
          return LOGIN_PROVIDER.GOED
        case ROUTES.LOGIN_INTERPARKING:
          return LOGIN_PROVIDER.INTERPARKING
        case ROUTES.LOGIN_NUNHEMS:
          return LOGIN_PROVIDER.NUNHEMS
        case ROUTES.LOGIN_AUTOSPHERE:
          return LOGIN_PROVIDER.AUTOSPHERE
        case ROUTES.LOGIN_ARGENTA:
          return LOGIN_PROVIDER.ARGENTA
      }
    return null
  }

  const login = async (payload, provider) => {
    const result = await authStore.login(payload, provider)
    if (result) {
      const userAuthId = result.userId
      // ✅ clear cart before clearing session/token
      if (userAuthId) {
        await connectionCartStore.clearCart(userAuthId)
      }

      sessionStorage.setItem(IS_AUTHORIZED, true)
      await getAuthSession()
      redirectAfterLogin()
    }
  }

  const logout = async sessionExpired => {
    resetSession()
    const provider = authStore.AppSession.session?.loginProvider

    const userAuthId = authStore.AppSession?.session?.id
    // ✅ clear cart before clearing session/token
    if (userAuthId && !sessionExpired) {
      await connectionCartStore.clearCart(userAuthId)
    }

    if (sessionExpired) {
      raiseLogoutRequest()
      redirectToLogin({ provider, reload: true })
      return true
    } else {
      const result = await authStore.logout()
      raiseLogoutRequest()

      if (result) {
        window.location.href = getLoginUrl(provider)
      } else {
        window.location.href = `${getLoginUrl(provider)}?logout=true`
      }
      return result
    }
  }

  const logoutAfterPunchout = async () => {
    const userAuthId = authStore.AppSession?.session?.id
    // Clear all carts before session reset
    await connectionCartStore.clearCart(userAuthId)

    resetSession()
    const result = await authStore.logout()
    raiseLogoutRequest()
    return result
  }

  const raiseLogoutRequest = async () => {
    // Trigger request to logout other open tabs
    localStorage.setItem(LOGOUT_REQUEST, true)

    // Clear trigger aftewards
    localStorage.removeItem(LOGOUT_REQUEST)
  }

  const redirectToLogin = ({ reload, provider, loginSearchParams, searchParams } = {}) => {
    if (!isSsoPath(window.location.pathname)) {
      const searchString = searchParams ? jsonToQueryString(searchParams) : window.location.search
      sessionStorage.setItem(REDIRECT_URL, `${window.location.pathname}${searchString}`)
    }

    let url = getLoginUrl(provider)
    if (loginSearchParams) {
      url = `${url}${jsonToQueryString(loginSearchParams)}`
    }

    if (reload) {
      window.location.href = url
    } else {
      navigate(url)
    }
  }

  const redirectAfterLogin = newUrl => {
    let redirectUrl
    if (newUrl) {
      redirectUrl = newUrl
    } else {
      redirectUrl = sessionStorage.getItem(REDIRECT_URL)
      sessionStorage.removeItem(REDIRECT_URL)

      if (!redirectUrl && isSsoPath(redirectUrl)) {
        redirectUrl = ROUTES.DEFAULT
      } else if (!redirectUrl && isSsoPath(location.pathname)) {
        redirectUrl = ROUTES.DEFAULT
      }
    }

    if (redirectUrl) {
      navigate(redirectUrl, { replace: true })
    }
  }

  const resetSession = () => {
    sessionStorage.clear()
  }

  return {
    getAuthSession,
    getLoginUrl,
    getLoginProvider,
    login,
    logout,
    logoutAfterPunchout,
    raiseLogoutRequest,
    redirectToLogin,
    redirectAfterLogin,
    resetSession,
  }
}
